import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "ipfs_menu_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);
    const [pulse, setPulse] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = useCallback((product) => {
        const variantLabel = product.variant_label || null;
        setItems((prev) => {
            const ex = prev.find(
                (p) => p.product_id === product.id && (p.variant_label || null) === variantLabel
            );
            if (ex) {
                return prev.map((p) =>
                    p.product_id === product.id && (p.variant_label || null) === variantLabel
                        ? { ...p, quantity: p.quantity + 1 }
                        : p
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    product_name: product.name,
                    price: product.price,
                    unit: product.unit,
                    variant_label: variantLabel,
                    photo_url: product.photo_url,
                    quantity: 1,
                },
            ];
        });
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
    }, []);

    const removeItem = useCallback((product_id, variant_label = null) => {
        setItems((prev) => prev.filter(
            (p) => !(p.product_id === product_id && (p.variant_label || null) === variant_label)
        ));
    }, []);

    const updateQty = useCallback((product_id, qty, variant_label = null) => {
        if (qty <= 0) {
            removeItem(product_id, variant_label);
            return;
        }
        setItems((prev) =>
            prev.map((p) =>
                p.product_id === product_id && (p.variant_label || null) === variant_label
                    ? { ...p, quantity: qty }
                    : p
            )
        );
    }, [removeItem]);

    const clearCart = useCallback(() => setItems([]), []);

    const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);

    return (
        <CartContext.Provider
            value={{ items, total, count, addItem, removeItem, updateQty, clearCart, open, setOpen, pulse }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be inside CartProvider");
    return ctx;
};
